import jsPDF from "jspdf";
import { pdf } from "@react-pdf/renderer";
import {
  Info,
  Printer,
  Loader2,
  FileText,
  FileSpreadsheet,
  Settings2,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { CURRENT_USER_NAME } from "../../utils/constants";
import QRCode from "qrcode";
import SaudiArabicBilingualInvoice from "./SaudiPrintLayout";
import AmiriRegular from "../../utils/Amri-Regular";
import InvoiceDoLayoutPrint from "./InvoiceDoLayoutPrint";
import InvoiceDoLayoutPrintHeader from "./InvoiceDoLayoutPrintHeader";
import CubixInvoicePrint2PDF from "./CubixInvoicePrint2";
import SalesInvoiceDetailedLayoutPrint from "./SalesInvoiceDetailedLayoutPrint";
import { exportInvoiceToExcel } from "./exportInvoiceToExcel";
const InvoicePrintPreview = ({
  invoiceNo,
  BASE_URL,
  COMPANY_CODE,
  DEPARTMENT,
  REPORT_FOOTER_IMAGE,
  REPORT_HEADER_IMAGE,
  COMPANY_NAME,
  VAT_PERCENTAGE,
  ZATCASTATUS,
  reportSealImage,
  reportWatermarkImage,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState("Invoice");
  const [selectedPrintTemplate, setSelectedPrintTemplate] =
    useState("template1");
  const [invoiceData, setInvoiceData] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [error, setError] = useState(null);
  const [showConfig, setShowConfig] = useState(true);

  const defaultPrintConfig = {
    showHeader: false,
    showFooter: false,
    showWatermark: false,
    showSeal: true,
    showSignature: true,
    showCode: false,
    showFOC: false,
    // showHeaderOnAllPages: true,
    // showFooterOnAllPages: true,
    // showInvoiceInfoOnAllPages: true,
  };
  const [printConfig, setPrintConfig] = useState(() => {
    try {
      const saved = localStorage.getItem("invoicePrintConfig");
      return saved ? JSON.parse(saved) : defaultPrintConfig;
    } catch {
      return defaultPrintConfig;
    }
  });

  useEffect(() => {
    localStorage.setItem("invoicePrintConfig", JSON.stringify(printConfig));
  }, [printConfig]);

  const togglePrintConfig = (key) => {
    setPrintConfig((prev) => {
      const newValue = !prev[key];
      const updated = { ...prev, [key]: newValue };

      // Enforce dependencies
      if (key === "showHeader") {
        // If header is turned OFF, also turn off headerOnAllPages
        if (!newValue) {
          updated.showHeaderOnAllPages = false;
        }
      }
      if (key === "showHeaderOnAllPages") {
        // If headerOnAllPages is turned ON, header must be ON
        if (newValue) {
          updated.showHeader = true;
        }
      }
      if (key === "showFooter") {
        // If footer is turned OFF, also turn off footerOnAllPages
        if (!newValue) {
          updated.showFooterOnAllPages = false;
        }
      }
      if (key === "showFooterOnAllPages") {
        // If footerOnAllPages is turned ON, footer must be ON
        if (newValue) {
          updated.showFooter = true;
        }
      }

      return updated;
    });
  };

  const resetPrintConfig = () => {
    setPrintConfig(defaultPrintConfig);
  };

  const invoiceDoTemplates = [
    { id: "template1", name: "Without Header" },
    { id: "template2", name: "With Header" },
  ];
  const saudiTemplates = [
    { id: "template1", name: "Saudi Template with Header" },
  ];
  const detailedPrintTemplates = [
    { id: "template1", name: "Detailed Print (Default)" },
  ];
  const fetchInvoiceData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const url = `${BASE_URL}/api/SalesInvoiceDetail/${COMPANY_CODE}/${invoiceNo}/${DEPARTMENT}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch invoice data: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Fetched invoice data:", data);
      setInvoiceData(data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching invoice data:", err);
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, COMPANY_CODE, invoiceNo, DEPARTMENT]);

  const fetchQRCodeData = useCallback(async () => {
    try {
      const url = `${BASE_URL}/api/CRMZatcaQRGen/${COMPANY_CODE}/INVOICE/${invoiceNo}/${DEPARTMENT}`;
      const response = await fetch(url);

      if (!response.ok) {
        console.warn("QR Code fetch failed, will proceed without QR code");
        return;
      }

      const data = await response.json();
      console.log("Fetched QR code data:", data);

      if (data && data.length > 0 && data[0].QR_BASE64) {
        setQrCodeData(data[0].QR_BASE64);
      }
    } catch (err) {
      console.warn("Error fetching QR code data:", err);
    }
  }, [BASE_URL, COMPANY_CODE, invoiceNo, DEPARTMENT]);

  useEffect(() => {
    if (invoiceNo) {
      fetchInvoiceData();

      if (COMPANY_CODE === "SAURISE") {
        fetchQRCodeData();
      }
    }
  }, [invoiceNo, fetchInvoiceData, fetchQRCodeData, COMPANY_CODE]);
  const generateQRCodeImage = async (qrData) => {
    if (!qrData) return null;

    try {
      const canvas = document.createElement("canvas");

      await QRCode.toCanvas(canvas, qrData, {
        width: 200,
        margin: 1,
        errorCorrectionLevel: "M",
      });

      return canvas.toDataURL("image/png");
    } catch (error) {
      console.error("Error generating QR code image:", error);
      return null;
    }
  };

  const handlePrint = async () => {
    console.log("handlePrint called");
    if (!invoiceData || invoiceData.length === 0) {
      alert("No invoice data available to print");
      return;
    }

    setPrinting(true);
    await new Promise((resolve) => setTimeout(resolve, 0));

    try {
      const qrCodeImage = await generateQRCodeImage(qrCodeData);

      let docType = "INVOICEPRINT";

      if (selectedTemplate === "Invoice DO") {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        generateInvoiceDoPDF(
          doc,
          pageWidth,
          pageHeight,
          selectedPrintTemplate,
          qrCodeImage,
          reportSealImage,
        );
        const pdfBlob = doc.output("bloburl");
        window.open(pdfBlob);
      } else if (selectedTemplate === "Saudi") {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        generateSaudiTemplates(
          doc,
          pageWidth,
          pageHeight,
          selectedPrintTemplate,
          qrCodeImage,
          reportSealImage,
        );
        const pdfBlob = doc.output("bloburl");
        window.open(pdfBlob);
      } else if (selectedTemplate === "Detailed Print") {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const layoutOptions = {
          showHeaderImage: printConfig.showHeader,
          showFooterImage: printConfig.showFooter,
          showWatermark: printConfig.showWatermark,
          showCodeColumn: printConfig.showCode,
        };
        SalesInvoiceDetailedLayoutPrint(
          doc,
          pageWidth,
          pageHeight,
          invoiceData,
          REPORT_FOOTER_IMAGE,
          REPORT_HEADER_IMAGE,
          COMPANY_NAME,
          VAT_PERCENTAGE,
          qrCodeImage,
          reportSealImage,
          reportWatermarkImage,
          layoutOptions,
        );
        const pdfBlob = doc.output("bloburl");
        window.open(pdfBlob);
      } else {
        const cubixProps = {
          invoiceData,
          REPORT_FOOTER_IMAGE,
          REPORT_HEADER_IMAGE,
          COMPANY_NAME,
          reportSealImage,
          reportWatermarkImage,
          showHeader: printConfig.showHeader,
          showFooter: printConfig.showFooter,
          showWatermark: printConfig.showWatermark,
          showSeal: printConfig.showSeal,
          showSignature: printConfig.showSignature,
          showCode: printConfig.showCode,
          showFOC: printConfig.showFOC,
          // showHeaderOnAllPages: printConfig.showHeaderOnAllPages,
          // showFooterOnAllPages: printConfig.showFooterOnAllPages,
          // showInvoiceInfoOnAllPages: printConfig.showInvoiceInfoOnAllPages,
        };
        const cubixElement = <CubixInvoicePrint2PDF {...cubixProps} />;
        const blob = await pdf(cubixElement).toBlob();
        const pdfBlobUrl = URL.createObjectURL(blob);
        window.open(pdfBlobUrl);
      }

      const logPayload = [
        {
          cmpcode: COMPANY_CODE,
          doc_no: invoiceNo || "",
          status: "printed",
          evaluationcomments: `Printed ${selectedTemplate} using ${selectedPrintTemplate}`,
          userid: CURRENT_USER_NAME,
          deptno: DEPARTMENT,
          doctype: docType,
        },
      ];

      fetch(`${BASE_URL}/api/CRMDocLogReg`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(logPayload),
      }).catch((err) => console.error("Failed to log print event:", err));
    } catch (err) {
      console.error("Error during print:", err);
    } finally {
      setPrinting(false);
    }
  };

  const handleExportExcel = async () => {
    if (!invoiceData || invoiceData.length === 0) {
      alert("No invoice data available to export");
      return;
    }
    setExportingExcel(true);
    try {
      exportInvoiceToExcel(invoiceData, COMPANY_NAME);
    } catch (err) {
      console.error("Error exporting Excel:", err);
    } finally {
      setExportingExcel(false);
    }
  };

  const generateSaudiTemplates = (
    doc,
    pageWidth,
    pageHeight,
    templateId,
    qrCodeImage,
    reportSealImage,
  ) => {
    if (!invoiceData || invoiceData.length === 0) return;

    SaudiArabicBilingualInvoice(
      doc,
      pageWidth,
      pageHeight,
      invoiceData,
      REPORT_FOOTER_IMAGE,
      REPORT_HEADER_IMAGE,
      COMPANY_NAME,
      qrCodeImage,
      AmiriRegular,
    );
  };
  const generateInvoiceDoPDF = (
    doc,
    pageWidth,
    pageHeight,
    templateId,
    qrCodeImage,
    reportSealImage,
  ) => {
    if (!invoiceData || invoiceData.length === 0) return;

    switch (templateId) {
      case "template1":
        InvoiceDoLayoutPrint(
          doc,
          pageWidth,
          pageHeight,
          invoiceData,
          REPORT_FOOTER_IMAGE,
          REPORT_HEADER_IMAGE,
          COMPANY_NAME,
          VAT_PERCENTAGE,
          qrCodeImage,
          reportSealImage,
        );
        break;
      case "template2":
        InvoiceDoLayoutPrintHeader(
          doc,
          pageWidth,
          pageHeight,
          invoiceData,
          REPORT_FOOTER_IMAGE,
          REPORT_HEADER_IMAGE,
          COMPANY_NAME,
          VAT_PERCENTAGE,
          qrCodeImage,
          reportSealImage,
        );
        break;

      default:
        InvoiceDoLayoutPrint(
          doc,
          pageWidth,
          pageHeight,
          invoiceData,
          REPORT_FOOTER_IMAGE,
          REPORT_HEADER_IMAGE,
          COMPANY_NAME,
          VAT_PERCENTAGE,
          qrCodeImage,
          reportSealImage,
        );
    }
  };
  return (
    <div className="bg-gradient-to-r from-emerald-50/50 to-teal-50/30 border border-emerald-200/60 rounded-xl overflow-hidden">
      <div className="bg-white/70 backdrop-blur-sm px-3 py-2 border-b border-emerald-200/40 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-emerald-100 flex items-center justify-center">
            <Printer className="w-3 h-3 text-emerald-600" />
          </div>
          <span className="text-xs font-semibold text-slate-700 tracking-wider">
            PRINT & PREVIEW
          </span>
        </div>
      </div>

      <div className="p-3 space-y-2">
        {loading && (
          <div className="flex items-center justify-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <span className="text-sm text-blue-700 font-medium">
              Loading invoice data...
            </span>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 border border-red-300 bg-red-50 rounded-lg text-sm text-red-800">
            <Info className="h-5 w-5 text-red-400" />
            <p>Error: {error}</p>
          </div>
        )}

        {!loading && !error && invoiceData && (
          <>
            <div className="flex items-center gap-2">
              <select
                value={selectedTemplate}
                onChange={(e) => {
                  setSelectedTemplate(e.target.value);
                  setSelectedPrintTemplate("template1");
                }}
                disabled={loading}
                className="flex-1 px-2 py-1.5 border border-gray-200 bg-white/60 backdrop-blur-sm rounded-lg text-xs focus:ring-2 focus:ring-emerald-500/50 focus:bg-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="Invoice">Invoice</option>
                <option value="Invoice DO">Invoice DO</option>
                {/* <option value="Detailed Print">Detailed Print</option> */}
                {ZATCASTATUS === "SAUDIPHASE1" && (
                  <option value="Saudi">Saudi Template</option>
                )}
              </select>

              {(selectedTemplate === "Invoice" ||
                selectedTemplate === "Detailed Print") && (
                <button
                  onClick={() => setShowConfig(!showConfig)}
                  className={`px-2 py-1.5 rounded-lg transition-all flex items-center gap-1 text-xs font-semibold shadow-sm border ${
                    showConfig
                      ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                      : "bg-white/60 border-gray-200 text-slate-600 hover:bg-white"
                  }`}
                >
                  <Settings2 className="w-3 h-3" />
                  Config
                </button>
              )}

              <button
                onClick={handlePrint}
                disabled={loading || printing}
                className="px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg hover:from-emerald-600 hover:to-emerald-700 transition-all flex items-center gap-1.5 text-xs font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {printing ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Printer className="w-3 h-3" />
                )}
                {printing ? "Printing..." : "Print"}
              </button>

              <button
                onClick={handleExportExcel}
                disabled={loading || exportingExcel || printing}
                className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all flex items-center gap-1.5 text-xs font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportingExcel ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-3 h-3" />
                )}
                {exportingExcel ? "Exporting..." : "Excel"}
              </button>
            </div>

            {(selectedTemplate === "Invoice" ||
              selectedTemplate === "Detailed Print") &&
              showConfig && (
                <div className="p-2 bg-white/60 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] font-medium text-slate-600">
                      Print Configuration
                    </span>
                    <button
                      onClick={resetPrintConfig}
                      className="text-[9px] text-emerald-600 hover:text-emerald-800 font-medium"
                    >
                      Reset Default
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {[
                      { key: "showHeader", label: "Header" },
                      { key: "showFooter", label: "Footer" },
                      { key: "showWatermark", label: "Watermark" },
                      { key: "showSeal", label: "Seal" },
                      { key: "showSignature", label: "Signature" },
                      { key: "showCode", label: "Code" },
                      { key: "showFOC", label: "FOC" },
                      // {
                      //   key: "showHeaderOnAllPages",
                      //   label: "Header on all pages",
                      //   disabled: !printConfig.showHeader,
                      // },
                      // {
                      //   key: "showFooterOnAllPages",
                      //   label: "Footer on all pages",
                      //   disabled: !printConfig.showFooter,
                      // },
                      // {
                      //   key: "showInvoiceInfoOnAllPages",
                      //   label: "Invoice info on all pages",
                      // },
                    ].map(({ key, label, disabled }) => (
                      <label
                        key={key}
                        className={`flex items-center gap-1.5 cursor-pointer group ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={printConfig[key]}
                          disabled={disabled}
                          onChange={() => togglePrintConfig(key)}
                          className="w-3 h-3 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500/50 disabled:cursor-not-allowed"
                        />
                        <span className="text-[10px] font-medium text-slate-600 group-hover:text-slate-800 transition-colors">
                          {label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

            {selectedTemplate === "Invoice DO" && (
              <div className="flex items-center gap-2 p-2 bg-white/60 rounded-lg border border-gray-200">
                <span className="text-[10px] font-medium text-slate-600">
                  Template:
                </span>
                <div className="flex gap-1 flex-1">
                  {invoiceDoTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setSelectedPrintTemplate(template.id)}
                      className={`flex-1 flex flex-col items-center gap-0.5 p-1 rounded border-2 transition-all ${
                        selectedPrintTemplate === template.id
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-gray-300 bg-white hover:border-emerald-400"
                      }`}
                    >
                      <FileText
                        className={`w-4 h-4 ${
                          selectedPrintTemplate === template.id
                            ? "text-emerald-600"
                            : "text-gray-400"
                        }`}
                      />
                      <span className="text-[8px] font-medium text-slate-600">
                        {template.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedTemplate === "Saudi" && (
              <div className="flex items-center gap-2 p-2 bg-white/60 rounded-lg border border-gray-200">
                <span className="text-[10px] font-medium text-slate-600">
                  Template:
                </span>
                <div className="flex gap-1 flex-1">
                  {saudiTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setSelectedPrintTemplate(template.id)}
                      className={`flex-1 flex flex-col items-center gap-0.5 p-1 rounded border-2 transition-all ${
                        selectedPrintTemplate === template.id
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-gray-300 bg-white hover:border-emerald-400"
                      }`}
                    >
                      <FileText
                        className={`w-4 h-4 ${
                          selectedPrintTemplate === template.id
                            ? "text-emerald-600"
                            : "text-gray-400"
                        }`}
                      />
                      <span className="text-[8px] font-medium text-slate-600">
                        {template.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* {selectedTemplate === "Detailed Print" && (
              <div className="flex items-center gap-2 p-2 bg-white/60 rounded-lg border border-gray-200">
                <span className="text-[10px] font-medium text-slate-600">
                  Template:
                </span>
                <div className="flex gap-1 flex-1">
                  {detailedPrintTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setSelectedPrintTemplate(template.id)}
                      className={`flex-1 flex flex-col items-center gap-0.5 p-1 rounded border-2 transition-all ${
                        selectedPrintTemplate === template.id
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-gray-300 bg-white hover:border-emerald-400"
                      }`}
                    >
                      <FileText
                        className={`w-4 h-4 ${
                          selectedPrintTemplate === template.id
                            ? "text-emerald-600"
                            : "text-gray-400"
                        }`}
                      />
                      <span className="text-[8px] font-medium text-slate-600">
                        {template.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )} */}
          </>
        )}

        {!loading && !error && !invoiceData && (
          <div className="flex items-center gap-2 p-3 border border-gray-300 bg-gray-50 rounded-lg text-sm text-gray-600">
            <Info className="h-5 w-5 text-gray-400" />
            <p>No invoice number provided</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicePrintPreview;
